import * as React from 'react'
import { Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AccessSection } from '@/components/billing/AccessSection'
import { AccountForm } from '@/components/billing/AccountForm'
import { BalancesSection } from '@/components/billing/BalancesSection'
import { LapsedSection } from '@/components/billing/LapsedSection'
import { MarginSection } from '@/components/billing/MarginSection'
import { formatMinor } from '@/components/billing/money'
import { PacksSection } from '@/components/billing/PacksSection'
import { SalesSection } from '@/components/billing/SalesSection'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBillingAccountsQuery, useCreateBillingAccount, useUpdateBillingAccount } from '@/queries/billing'
import type { BillingAccountInput } from '@/types/billing'

/** The credits bank: what an account sold, what it cost, and how it is set up. */
export function BillingPage() {
  const { t, i18n } = useTranslation()
  const { data: accounts = [], isLoading, isError } = useBillingAccountsQuery()
  const create = useCreateBillingAccount()
  const update = useUpdateBillingAccount()
  const [selectedId, setSelectedId] = React.useState<number | null>(null)
  const [form, setForm] = React.useState<'create' | 'edit' | null>(null)
  const [newKey, setNewKey] = React.useState<string | null>(null)

  const account = accounts.find((a) => a.id === selectedId) ?? accounts[0] ?? null

  const submit = async (input: BillingAccountInput) => {
    try {
      if (form === 'edit' && account) {
        await update.mutateAsync({ id: account.id, input })
        toast.success(t('billing.account.saved'))
      } else {
        const created = await create.mutateAsync(input)
        setSelectedId(created.account.id)
        setNewKey(created.key)
      }
      setForm(null)
    } catch {
      toast.error(t('billing.errors.save'))
    }
  }

  const fee = account
    ? t('billing.account.fee_summary', {
      fixed: formatMinor(account.pack_fee_units, account.packs[0]?.currency ?? 'EUR', i18n.language),
      percent: (account.pack_fee_bps / 100).toLocaleString(i18n.language),
    })
    : ''

  return (
    <div className="container mx-auto space-y-8 p-4 md:p-6">
      <PageHeader title={t('billing.title')} description={t('billing.subtitle')}>
        {accounts.length > 1 && account && (
          <Select value={String(account.id)} onValueChange={(value) => setSelectedId(Number(value))}>
            <SelectTrigger className="w-64" aria-label={t('billing.account.select')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="lg" onClick={() => setForm('create')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('billing.account.create')}
        </Button>
      </PageHeader>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : isError ? (
        <p className="text-sm text-destructive">{t('billing.errors.load')}</p>
      ) : !account ? (
        <EmptyState title={t('billing.empty_title')} description={t('billing.empty_description')} />
      ) : (
        <>
          <MarginSection accountId={account.id} />
          <SalesSection accountId={account.id} />
          <BalancesSection accountId={account.id} />
          <LapsedSection accountId={account.id} />
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold">{t('billing.account.settings_title')}</h2>
              <Button variant="outline" size="sm" onClick={() => setForm('edit')}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('billing.common.edit')}
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('billing.account.included')}</p>
                <p className="text-sm">
                  {Object.keys(account.included_by_plan).length === 0
                    ? t('billing.account.no_included')
                    : Object.entries(account.included_by_plan).map(([plan, credits]) =>
                      t('billing.account.included_line', { plan, credits })).join(' · ')}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('billing.account.fee')}</p>
                <p className="text-sm">{fee}</p>
              </div>
            </div>
          </section>
          <PacksSection account={account} />
          <AccessSection account={account} revealKey={newKey} />
        </>
      )}

      <AccountForm
        open={form !== null}
        onOpenChange={(open) => !open && setForm(null)}
        account={form === 'edit' ? account : null}
        onSubmit={submit}
        saving={create.isPending || update.isPending}
      />
    </div>
  )
}
